import { useState } from 'react';
import { X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { patchTitular } from '@lib/services/portal.service';
import { SugerenciaEmail } from './SugerenciaEmail';

/**
 * Modal que se abre cuando el email de contacto del titular ya está siendo
 * usado por otro socio de Medicus dado de alta. Pide un email nuevo y lo
 * guarda con `patchTitular`; el server vuelve a validar contra la base en cada
 * intento (rechaza si el nuevo email también está tomado o tiene formato
 * inválido). Recién cuando el guardado pasa, el titular puede continuar.
 *
 * Mismo lenguaje visual que `TyCMs2Modal` (overlay oscuro + card blanca con
 * header/X + footer con botones) para mantener la coherencia del wizard.
 */
interface Props {
  token: string;
  open: boolean;
  mensaje: string;
  /** El email nuevo quedó guardado y libre → continuar al plan. */
  onResuelto: () => void;
  onCancelar: () => void;
}

// Mismo predicado que el server (sin regex con cuantificadores anidados).
function emailValido(s: string): boolean {
  if (!s) return false;
  if (/\s/.test(s)) return false;
  const at = s.indexOf('@');
  if (at <= 0 || at !== s.lastIndexOf('@')) return false;
  const domain = s.slice(at + 1);
  if (!domain) return false;
  const dot = domain.lastIndexOf('.');
  return dot > 0 && dot < domain.length - 1;
}

export function EmailEnUsoModal({ token, open, mensaje, onResuelto, onCancelar }: Props) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (nuevo: string) => patchTitular(token, { email: nuevo }),
    onSuccess: () => {
      setError(null);
      // El email del titular cambió → invalidamos el legajo cacheado.
      qc.invalidateQueries({ queryKey: ['onboarding', token] });
      onResuelto();
    },
    onError: (err: unknown) => {
      const e = err as {
        response?: { data?: { error?: { message?: string }; message?: string } };
      };
      setError(
        e.response?.data?.error?.message ??
          e.response?.data?.message ??
          'No pudimos validar el email. Probá con otro.',
      );
    },
  });

  if (!open) return null;

  const limpio = email.trim();

  const handleGuardar = () => {
    if (mut.isPending) return;
    if (!emailValido(limpio)) {
      setError('Ingresá un email válido (ej. nombre@dominio.com).');
      return;
    }
    mut.mutate(limpio);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-en-uso-titulo"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 12,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 16,
          maxWidth: 460,
          width: '100%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.35)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-outline)',
          }}
        >
          <h2
            id="email-en-uso-titulo"
            className="heading-4"
            style={{ margin: 0, color: 'var(--color-primary-darker)' }}
          >
            Necesitamos otro email
          </h2>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={onCancelar}
            disabled={mut.isPending}
            style={{
              background: 'none',
              border: 'none',
              cursor: mut.isPending ? 'not-allowed' : 'pointer',
              color: 'var(--color-element-mid)',
              padding: 4,
              display: 'flex',
            }}
          >
            <X size={20} />
          </button>
        </header>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.5,
              color: 'var(--color-element-high)',
            }}
          >
            {mensaje}
          </p>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-element-high)' }}>
              Nuevo email de contacto
            </span>
            <input
              className="input"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="nombre@dominio.com"
              value={email}
              maxLength={120}
              disabled={mut.isPending}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGuardar();
              }}
            />
            <SugerenciaEmail
              email={email}
              onUsar={(corregido) => {
                setEmail(corregido);
                setError(null);
              }}
            />
          </label>

          {error && (
            <p className="status status-error" style={{ display: 'block', margin: 0 }}>
              {error}
            </p>
          )}
        </div>

        <footer
          style={{
            borderTop: '1px solid var(--color-outline)',
            padding: '14px 20px',
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancelar}
            disabled={mut.isPending}
            style={{
              backgroundColor: '#d1d5db',
              borderColor: '#9ca3af',
              color: 'var(--color-primary-darker)',
              fontWeight: 700,
            }}
          >
            <span className="btn-font-style">Cancelar</span>
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGuardar}
            disabled={mut.isPending}
            style={{ cursor: mut.isPending ? 'not-allowed' : 'pointer' }}
          >
            <span className="btn-font-style">
              {mut.isPending ? 'Validando…' : 'Guardar y continuar'}
            </span>
          </button>
        </footer>
      </div>
    </div>
  );
}
